import React, { useState, useRef } from 'react';
import { 
  Upload, Image as ImageIcon, Copy, Check, 
  FileImage, AlertCircle, Loader2, X, Info
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import api from '@/lib/api';

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml'];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

const ASSET_TYPES = [
  { 
    value: 'thumbnail', 
    label: 'Thumbnail',
    description: 'Camera preview images',
    path: '/assets/thumbs/'
  },
  { 
    value: 'icon', 
    label: 'Icon',
    description: 'UI icons and badges',
    path: '/assets/icons/'
  }
];

export function Assets() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [assetType, setAssetType] = useState('thumbnail');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    setError('');
    setUploadResult(null);

    // Validate file type
    if (!ALLOWED_TYPES.includes(selectedFile.type)) {
      setError('Invalid file type. Allowed: JPG, PNG, WebP, SVG');
      return;
    }

    // Validate file size
    if (selectedFile.size > MAX_FILE_SIZE) {
      setError('File too large. Maximum size: 5MB');
      return;
    }

    setFile(selectedFile);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
    };
    reader.readAsDataURL(selectedFile);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      handleFileSelect({ target: { files: [droppedFile] } });
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleUpload = async () => {
    if (!file) return;

    setIsUploading(true);
    setError('');

    try {
      const result = await api.uploadFile(file, assetType);
      
      // Extract public URL from response
      const publicUrl = result.public_url || result.url || result.path || result.file_url;
      
      setUploadResult({
        publicUrl,
        relativePath: result.relative_path || result.path,
        filename: result.filename || file.name,
        mimeType: result.mime_type || file.type,
        sizeBytes: result.size_bytes || file.size,
        width: result.width,
        height: result.height
      });
      
      toast.success('File uploaded successfully');
    } catch (err) {
      setError(err.message || 'Upload failed');
      toast.error(err.message || 'Upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  const handleCopy = async () => {
    if (!uploadResult?.publicUrl) return;

    try {
      await navigator.clipboard.writeText(uploadResult.publicUrl);
      setCopied(true);
      toast.success('URL copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy URL');
    }
  };

  const handleClear = () => {
    setFile(null);
    setPreview(null);
    setUploadResult(null);
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  return (
    <div className="space-y-6" data-testid="assets-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Assets</h1>
        <p className="text-muted-foreground">Upload thumbnails and icons for cameras</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Upload Section */}
        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Upload Asset
            </CardTitle>
            <CardDescription>
              Upload JPG, PNG, WebP, or SVG files (max 5MB)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Asset Type Selector */}
            <div className="space-y-2">
              <Label>Asset Type</Label>
              <Select value={assetType} onValueChange={setAssetType}>
                <SelectTrigger data-testid="asset-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ASSET_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      <div className="flex items-center gap-2">
                        <span>{type.label}</span>
                        <span className="text-xs text-muted-foreground">
                          ({type.path})
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {ASSET_TYPES.find(t => t.value === assetType)?.description}
              </p>
            </div>

            {/* Drop Zone */}
            <div
              className={`
                relative flex min-h-[200px] cursor-pointer flex-col items-center justify-center 
                rounded-lg border-2 border-dashed transition-colors
                ${file ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50 hover:bg-secondary/50'}
              `}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onClick={() => fileInputRef.current?.click()}
              data-testid="upload-dropzone"
            >
              <input
                ref={fileInputRef}
                type="file"
                accept={ALLOWED_TYPES.join(',')}
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-input"
              />

              {preview ? (
                <div className="relative w-full p-4">
                  <img
                    src={preview}
                    alt="Preview"
                    className="mx-auto max-h-[160px] rounded-lg object-contain"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute right-2 top-2 h-8 w-8 rounded-full bg-background/80"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClear();
                    }}
                    data-testid="clear-file-btn"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-3 p-6 text-center">
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary">
                    <FileImage className="h-7 w-7 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="font-medium text-foreground">
                      Drop an image here or click to browse
                    </p>
                    <p className="text-sm text-muted-foreground">
                      JPG, PNG, WebP, SVG up to 5MB
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* File Info */}
            {file && (
              <div className="rounded-lg bg-secondary/50 p-3">
                <div className="flex items-center gap-3">
                  <ImageIcon className="h-5 w-5 text-muted-foreground" />
                  <div className="flex-1 min-w-0">
                    <p className="truncate font-medium text-sm">{file.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatFileSize(file.size)} • {file.type.split('/')[1].toUpperCase()}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="flex items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                {error}
              </div>
            )}

            {/* Upload Button */}
            <Button
              className="w-full"
              onClick={handleUpload}
              disabled={!file || isUploading}
              data-testid="upload-btn"
            >
              {isUploading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  Upload {ASSET_TYPES.find(t => t.value === assetType)?.label}
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Result Section */}
        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ImageIcon className="h-5 w-5" />
              Uploaded File
            </CardTitle>
            <CardDescription>
              Use this URL in camera settings
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {uploadResult ? (
              <>
                {/* Preview */}
                <div className="overflow-hidden rounded-lg border border-border bg-black/30">
                  <img
                    src={uploadResult.publicUrl}
                    alt="Uploaded asset"
                    className="h-48 w-full object-contain p-4"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>

                {/* File Details */}
                {(uploadResult.width || uploadResult.sizeBytes) && (
                  <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                    {uploadResult.width && uploadResult.height && (
                      <span>{uploadResult.width}×{uploadResult.height}px</span>
                    )}
                    {uploadResult.sizeBytes && (
                      <span>{formatFileSize(uploadResult.sizeBytes)}</span>
                    )}
                    {uploadResult.mimeType && (
                      <span>{uploadResult.mimeType}</span>
                    )}
                  </div>
                )}

                {/* Public Asset URL - Primary Field */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    Public Asset URL
                    <span className="text-xs text-primary font-normal">(copy this)</span>
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      value={uploadResult.publicUrl}
                      readOnly
                      className="font-mono text-sm bg-secondary/30"
                      onClick={(e) => e.target.select()}
                      data-testid="uploaded-url-input"
                    />
                    <Button
                      variant="secondary"
                      onClick={handleCopy}
                      className="shrink-0"
                      data-testid="copy-url-btn"
                    >
                      {copied ? (
                        <>
                          <Check className="mr-1 h-4 w-4" />
                          Copied
                        </>
                      ) : (
                        <>
                          <Copy className="mr-1 h-4 w-4" />
                          Copy
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                {/* Helper Text */}
                <div className="rounded-lg bg-primary/10 border border-primary/20 p-4">
                  <div className="flex gap-3">
                    <Info className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-foreground mb-1">
                        How to use this URL
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Copy the URL above and paste it into the{' '}
                        <code className="rounded bg-background/50 px-1.5 py-0.5 font-mono text-xs text-foreground">
                          thumbnail_path
                        </code>{' '}
                        field when editing a camera in the Cameras page.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Clear Button */}
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={handleClear}
                >
                  Upload Another File
                </Button>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-secondary">
                  <ImageIcon className="h-8 w-8 text-muted-foreground/30" />
                </div>
                <p className="font-medium text-foreground">No file uploaded yet</p>
                <p className="text-sm text-muted-foreground">
                  Upload an image to get its public URL
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Info Section */}
      <Card className="border-border bg-card">
        <CardHeader>
          <CardTitle className="text-lg">Thumbnail Guidelines</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="rounded-lg bg-secondary/50 p-4">
              <h4 className="mb-2 font-medium">Recommended Size</h4>
              <p className="text-sm text-muted-foreground">
                640×360 pixels (16:9 aspect ratio) for optimal display
              </p>
            </div>
            <div className="rounded-lg bg-secondary/50 p-4">
              <h4 className="mb-2 font-medium">File Format</h4>
              <p className="text-sm text-muted-foreground">
                JPG or WebP for photos, PNG for graphics with transparency
              </p>
            </div>
            <div className="rounded-lg bg-secondary/50 p-4">
              <h4 className="mb-2 font-medium">Storage Paths</h4>
              <div className="text-xs text-muted-foreground font-mono space-y-1">
                <p>Thumbs: /assets/thumbs/</p>
                <p>Icons: /assets/icons/</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default Assets;
